import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce, get, isEmpty, isEqual } from 'lodash';
import { CircularProgress } from '@material-ui/core';
import { IconButton } from '@coolremote/shared';
import { Schedule as ScheduleIcon } from '@material-ui/icons';
import { t } from 'ttag';
import { useHistory } from 'react-router-dom';
import { useStoreActions } from '@models/RootStore';
import { useGetBacnetUiTemplate } from '@hooks/useBacnetUiTemplatesApi';
import { useGetBacnetParamTemplate } from '@hooks/useBacnetParamTemplatesApi';
import { useSetBacnetObjectValue } from '@hooks/useBacnetDevicesApi';
import {
  bacnetUnits,
  controlTypes,
  mergeUiTemplatePointsWithParamTemplate,
} from '@components/AddEditBacnetUiTemplateDialog/constants';
import NumericControl from './components/NumericControl';
import ListControl from './components/ListControl';
import InformationTextControl from './components/InformationTextControl';
import { useBacnetSchedules } from '@hooks/useBacnetSchedules';
import useUniversalUnitShape from '@hooks/useUniversalUnitShape';
import UnitPanel from '../UnitPanel/UnitPanel';
import BacnetUnitControl from '../../../../screens/Control/ControlComponents/BacnetControlPanel/BacnetUnitControl';
import useStyles from './styles';

const MobileBacnetControlPanel = ({
  entry,
  isCelsius,
  tempSymbol,
  siteTemp,
  navigateToSchedules: propNavigateToSchedules,
}) => {
  const { title, name, id } = entry;
  const classes = useStyles();
  const history = useHistory();

  const handleNavigateToSchedules = useCallback(() => {
    if (propNavigateToSchedules) {
      propNavigateToSchedules();
      return;
    }
    history.push(`/bacnet-unit/${id}/schedules`, { bacnetUnit: entry });
  }, [propNavigateToSchedules, history, id, entry]);

  const addWebSocketListener = useStoreActions((a) => a.addWebSocketListener);
  const removeWebSocketListener = useStoreActions(
    (a) => a.removeWebSocketListener
  );

  const [activePickerId, setActivePickerId] = useState(null);
  const [operationalData, setOperationalData] = useState(
    entry?.operationalData || {}
  );
  const { activeSchedules } = useBacnetSchedules(id);

  const uiTemplateId = typeof entry?.uiTemplate === 'string' ? entry?.uiTemplate : null;
  const { data: uiTemplateData, isLoading: isLoadingUiTemplate } = useGetBacnetUiTemplate(
    uiTemplateId
  );
  const uiTemplate = typeof entry?.uiTemplate === 'object' && entry?.uiTemplate !== null ? entry.uiTemplate : uiTemplateData;

  const paramTemplateId = typeof entry?.paramTemplate === 'string' ? entry?.paramTemplate : uiTemplate?.paramTemplate;
  const { data: paramTemplateData, isLoading: isLoadingParamTemplate } = useGetBacnetParamTemplate(
    typeof paramTemplateId === 'string' ? paramTemplateId : null
  );
  const paramTemplate = typeof entry?.paramTemplate === 'object' && entry?.paramTemplate !== null
    ? entry.paramTemplate
    : paramTemplateData || (entry?.paramTemplateData ? { entityTemplate: entry.paramTemplateData?.entityTemplate === true, data: entry.paramTemplateData } : undefined);

  const {
    synthesizedUnit,
    isUniversalUnit,
    hasUniversalControls,
    unitPanelStoreProps,
    handleSetActiveOperationStatus,
    handleSetActiveSetpoint,
    handleSetActiveOperationMode,
    handleSetActiveFanMode,
    handleSetActiveSwingMode,
  } = useUniversalUnitShape({
    entry,
    paramTemplate,
    navigateToSchedules: handleNavigateToSchedules,
    isCelsius,
    protocol: 'bacnet',
  });

  const { mutate: setObjectValue } = useSetBacnetObjectValue();
  const setObjectValueRef = useRef(setObjectValue);
  setObjectValueRef.current = setObjectValue;

  useEffect(() => {
    if (
      entry?.operationalData &&
      !isEqual(operationalData, entry.operationalData)
    ) {
      setOperationalData(entry.operationalData);
    }
  }, [entry?.operationalData]);

  const debouncedSetObjectValue = useMemo(
    () =>
      debounce((objectId, value) => {
        setObjectValueRef.current({ unitId: id, payload: { object: objectId, value } });
      }, 1000),
    [id]
  );

  useEffect(() => {
    return () => debouncedSetObjectValue.cancel();
  }, [debouncedSetObjectValue]);

  const prepareObjectValue = (objectId, value) => {
    try {
      if (objectId.startsWith('binary')) {
        if (typeof value === 'string') {
          value = parseInt(value);
        }
      }
    } catch (error) {
      console.error('Error parsing object value:', error);
    }
    return value;
  };

  const handleValueChange = (objectId, value) => {
    const preparedValue = prepareObjectValue(objectId, value);
    setOperationalData((prev) => ({
      ...prev,
      [`${objectId}/85`]: preparedValue,
    }));
    debouncedSetObjectValue(objectId, preparedValue);
  };

  useEffect(() => {
    const handleWebSocketMessage = (message) => {
      if (
        message.name !== 'UPDATE_BACNET_UNIT_DATA' ||
        String(message.data?.unitId) !== String(id)
      ) {
        return;
      }
      const { operationalData: operationalDataFromMessage } = message.data;
      if (isEmpty(operationalDataFromMessage)) {
        return;
      }
      // BACnet pushes only the points that changed, so merge over what we already have
      setOperationalData((prev) => {
        const merged = { ...prev, ...operationalDataFromMessage };
        return isEqual(prev, merged) ? prev : merged;
      });
    };
    addWebSocketListener(handleWebSocketMessage);
    return () => removeWebSocketListener(handleWebSocketMessage);
  }, []);

  const mergedDataPoints = useMemo(() => {
    const points = mergeUiTemplatePointsWithParamTemplate(
      paramTemplate?.data,
      uiTemplate?.data?.points
    );
    return Object.fromEntries(
      Object.entries(points).sort(([, a], [, b]) =>
        get(a, 'label', '').localeCompare(get(b, 'label', ''))
      )
    );
  }, [paramTemplate, uiTemplate]);

  const rawPointsData = useMemo(() => {
    return (
      paramTemplate?.data ||
      entry?.paramTemplateData?.data ||
      entry?.paramTemplateData ||
      entry?.data ||
      {}
    );
  }, [paramTemplate, entry?.paramTemplateData, entry?.data]);

  const mappedPointsList = useMemo(() => {
    if (!rawPointsData || typeof rawPointsData !== 'object') {
      return [];
    }
    const entries = Object.entries(rawPointsData).filter(
      ([key, val]) =>
        val &&
        typeof val === 'object' &&
        key !== 'entityTemplate' &&
        key !== 'entityType'
    );

    return entries.map(([objectId, pointConfig]) => {
      const config = pointConfig || {};
      const { step, min, max } = config.customEnum || {};
      return {
        objectId,
        name: config.name || config.label || objectId,
        shortId: config.shortId,
        readOnly: config.readOnly ?? false,
        units: config.userUnits ?? config.units,
        userUnits: config.userUnits,
        minValue: config.minValue ?? (min != null ? parseFloat(min) : null),
        maxValue: config.maxValue ?? (max != null ? parseFloat(max) : null),
        step: config.step ?? (step != null ? parseFloat(step) : null),
        customEnum: config.customEnum,
        customEnumVisibility: config.customEnumVisibility,
        property: config.property,
      };
    });
  }, [rawPointsData]);

  const handleNumericClick = (pointId) => {
    setActivePickerId((prev) => (prev === pointId ? null : pointId));
  };

  const handlePickerClose = () => {
    setActivePickerId(null);
  };

  const renderControl = (dataPointId, dataPoint) => {
    const {
      controlType,
      label,
      config = {},
    } = dataPoint;
    const currentValue = operationalData[`${dataPointId}/85`];
    const isEditable = !config.readOnly;
    const symbol =
      bacnetUnits[config?.userUnits || config?.units]?.symbol || '';

    switch (controlType) {
      case controlTypes.toggle:
      case controlTypes.list:
      case controlTypes.onOff:
        return (
          <ListControl
            pointId={dataPointId}
            currentValue={currentValue}
            config={config}
            label={label}
            isEditable={isEditable}
            handleValueChange={handleValueChange}
            controlType={controlType}
            activePickerId={activePickerId}
            onPickerToggle={handleNumericClick}
          />
        );

      case controlTypes.numericValue:
        return (
          <NumericControl
            pointId={dataPointId}
            currentValue={currentValue}
            config={config}
            label={label}
            isEditable={isEditable}
            handleValueChange={handleValueChange}
            activePickerId={activePickerId}
            handleNumericClick={handleNumericClick}
            handlePickerClose={handlePickerClose}
            symbol={symbol}
          />
        );

      case controlTypes.informationText:
        return (
          <InformationTextControl
            pointId={dataPointId}
            currentValue={currentValue}
            label={label}
            config={config}
            symbol={symbol}
          />
        );

      default:
        return null;
    }
  };

  if (isLoadingUiTemplate || isLoadingParamTemplate) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }


  const showUniversalFallback = !uiTemplate && isUniversalUnit;

  const scheduleActionRow = handleNavigateToSchedules && (
    <div className={classes.scheduleActionRow}>
      <IconButton onClick={handleNavigateToSchedules} className={classes.scheduleButton}>
        <ScheduleIcon style={{ fontSize: 32 }} />
        {activeSchedules.length > 0 && <span className={classes.badge}>{activeSchedules.length}</span>}
      </IconButton>
    </div>
  );

  const renderBody = () => {
    if (uiTemplate) {
      return (
        <div className={classes.controlsList}>
          {Object.entries(mergedDataPoints).map(([dataPointId, dataPoint]) => (
            <div key={dataPointId} className={classes.controlRow}>
              {renderControl(dataPointId, dataPoint)}
            </div>
          ))}
          {scheduleActionRow}
        </div>
      );
    }

    if (isUniversalUnit) {
      if (hasUniversalControls && synthesizedUnit) {
        return (
          <UnitPanel
            {...unitPanelStoreProps}
            unit={synthesizedUnit}
            isCelsius={isCelsius}
            tempSymbol={tempSymbol}
            siteTemp={siteTemp}
            setActiveOperationStatus={handleSetActiveOperationStatus}
            setActiveSetpoint={handleSetActiveSetpoint}
            setActiveOperationMode={handleSetActiveOperationMode}
            setActiveFanMode={handleSetActiveFanMode}
            setActiveSwingMode={handleSetActiveSwingMode}
            navigateToSchedules={handleNavigateToSchedules}
          />
        );
      }
      return (
        <div className={classes.noTemplate}>
          {t`No universal controls configured for this unit.`}
        </div>
      );
    }

    if (!mappedPointsList.length) {
      return (
        <div className={classes.noTemplate}>
          {t`No BACnet parameters mapped for this unit.`}
        </div>
      );
    }

    return (
      <>
        <BacnetUnitControl
          unitName={title ?? name}
          points={mappedPointsList}
          operationalData={operationalData}
          onSetValue={handleValueChange}
        />
        {scheduleActionRow}
      </>
    );
  };

  return (
    <div className={classes.container}>
      {/* UnitPanel renders its own name, so the panel header would duplicate it. */}
      {!showUniversalFallback && (
        <div className={classes.header}>
          <div className={classes.unitName}>{title ?? name}</div>
        </div>
      )}
      {renderBody()}
    </div>
  );
};

export default MobileBacnetControlPanel;
